(() => {
  const STORAGE_KEY = 'AuroraTheme-RecentlyViewed';
  const CARD_SECTION = 'product-card-template';
  const MAX_ITEMS = 12;
  const SEARCH_DRAWER_BUTTON_SELECTOR = '[data-sidebar-button][data-sidebar-id="SearchSidebar"]';
  const RECENTS_SECTION_SELECTOR = '#SearchSidebar .search-sidebar-section--recently-viewed';
  const RECENTS_SKELETON_SELECTOR = '[data-search-recents-skeleton-card]';

  let prewarmPromise = null;
  const productCardSectionPromises = new Map();
  let searchSidebarFetchPatched = false;
  let articleSearchScrollLockY = 0;

  const getSearchSidebar = () => document.getElementById('SearchSidebar');

  const isArticleSearchSidebar = () =>
    getSearchSidebar()?.dataset.mysticSearchResourceType === 'article';

  const setArticleSearchScrollLock = (isLocked) => {
    const root = document.documentElement;

    if (isLocked) {
      if (root.classList.contains('mystic-article-search-open')) return;

      articleSearchScrollLockY = window.scrollY || window.pageYOffset || 0;
      root.classList.add('mystic-article-search-open');
      document.body.style.setProperty('--mystic-article-search-scroll-y', `-${articleSearchScrollLockY}px`);
      document.body.classList.add('mystic-article-search-scroll-locked');
      return;
    }

    if (!root.classList.contains('mystic-article-search-open')) return;

    root.classList.remove('mystic-article-search-open');
    document.body.classList.remove('mystic-article-search-scroll-locked');
    document.body.style.removeProperty('--mystic-article-search-scroll-y');
    window.scrollTo(0, articleSearchScrollLockY);
  };

  const watchArticleSearchScrollLock = () => {
    const sidebar = getSearchSidebar();
    if (!sidebar || sidebar.dataset.mysticSearchResourceType !== 'article') return;

    const update = () => setArticleSearchScrollLock(sidebar.classList.contains('is-opened'));
    const observer = new MutationObserver(update);
    observer.observe(sidebar, { attributes: true, attributeFilter: ['class'] });
    update();
  };

  const patchSearchSidebarPredictiveFetch = () => {
    if (searchSidebarFetchPatched || !window.fetch) return;

    const nativeFetch = window.fetch.bind(window);

    window.fetch = (input, initOptions) => {
      const rawUrl = typeof input === 'string' ? input : input?.url;

      if (isArticleSearchSidebar() && rawUrl?.includes('/search/suggest')) {
        const sidebar = getSearchSidebar();
        const url = new URL(rawUrl, window.location.origin);
        const sectionId = url.searchParams.get('section_id');

        if (sectionId && sectionId === sidebar?.dataset.sectionId) {
          url.searchParams.set('resources[type]', 'article');

          if (typeof input === 'string') {
            return nativeFetch(url.toString(), initOptions);
          }

          return nativeFetch(new Request(url.toString(), input), initOptions);
        }
      }

      return nativeFetch(input, initOptions);
    };

    searchSidebarFetchPatched = true;
  };

  const bindArticleFilterButtons = () => {
    const sidebar = getSearchSidebar();
    if (!sidebar || sidebar.dataset.mysticSearchResourceType !== 'article') return;

    sidebar.addEventListener('click', (event) => {
      const button = event.target.closest('.search-sidebar__article-filter[data-sidebar-search-query]');
      if (!button) return;

      const input = sidebar.querySelector('[data-sidebar-search-input]');
      if (!input) return;

      input.value = button.dataset.sidebarSearchQuery || button.textContent.trim();
      input.dispatchEvent(new Event('input', { bubbles: true }));
      input.focus();
    });
  };

  const getRootUrl = () => {
    const routes = window.Shopify?.routes;
    return routes?.root || routes?.root_url || '/';
  };

  const normalizeRoot = (root) => {
    if (!root) return '/';
    return root.endsWith('/') ? root : `${root}/`;
  };

  const getRecentlyViewedHandles = (limit = MAX_ITEMS) => {
    try {
      const handles = JSON.parse(window.localStorage.getItem(STORAGE_KEY) || '[]');
      if (!Array.isArray(handles)) return [];
      return handles.filter(Boolean).slice(0, limit);
    } catch (_error) {
      return [];
    }
  };

  const getProductCardUrl = (handle) => {
    const root = normalizeRoot(getRootUrl());
    return `${window.location.origin}${root}products/${encodeURIComponent(handle)}?sections=${CARD_SECTION}`;
  };

  const getProductCardSection = (handle) => {
    if (productCardSectionPromises.has(handle)) {
      return productCardSectionPromises.get(handle);
    }

    const sectionPromise = fetch(getProductCardUrl(handle), {
      credentials: 'same-origin',
      cache: 'force-cache',
    })
      .then((response) => response.json())
      .then((section) => section?.[CARD_SECTION] || null)
      .catch((error) => {
        productCardSectionPromises.delete(handle);
        throw error;
      });

    productCardSectionPromises.set(handle, sectionPromise);
    return sectionPromise;
  };

  const prewarmRecentlyViewed = () => {
    const handles = getRecentlyViewedHandles();
    if (!handles.length) {
      prewarmPromise = Promise.resolve();
      return prewarmPromise;
    }

    const uncachedHandles = handles.filter((handle) => !productCardSectionPromises.has(handle));
    if (prewarmPromise && !uncachedHandles.length) return prewarmPromise;

    prewarmPromise = Promise.allSettled(handles.map((handle) => getProductCardSection(handle))).then(() => undefined);

    return prewarmPromise;
  };

  const revealRecentlyViewedSkeleton = () => {
    const sidebar = getSearchSidebar();
    if (sidebar?.dataset.mysticSearchResourceType === 'article') return;

    const section = document.querySelector(RECENTS_SECTION_SELECTOR);
    if (!section || !section.querySelector(RECENTS_SKELETON_SELECTOR)) return;
    if (!getRecentlyViewedHandles().length) return;

    section.classList.remove('hidden');
  };

  const patchNativeRecentlyViewedFetch = () => {
    const RecentlyViewed = window.recentlyViewed;
    const prototype = RecentlyViewed?.prototype;

    if (!prototype || prototype.mysticParallelFetchItems) return;

    prototype.fetchItems = async function fetchItemsProgressively() {
      const handles = getRecentlyViewedHandles(this.cardLimit);
      if (!handles.length) return;

      const productsByIndex = new Array(handles.length);

      const renderProducts = () => {
        this.products = productsByIndex.filter(Boolean);
        if (!this.products.length) return;

        this.setItems();
        this.setVisible(true);
      };

      try {
        await Promise.allSettled(
          handles.map(async (handle, index) => {
            const sectionHtml = await getProductCardSection(handle);
            const node = this.createNodeByString(sectionHtml);
            if (node) {
              productsByIndex[index] = node;
              renderProducts();
            }
          })
        );
      } catch (error) {
        console.log(error);
      }
    };

    prototype.mysticParallelFetchItems = true;
  };

  const scheduleIdlePrewarm = () => {
    if (isArticleSearchSidebar()) return;

    const run = () => prewarmRecentlyViewed();

    if ('requestIdleCallback' in window) {
      window.requestIdleCallback(run, { timeout: 2500 });
      return;
    }

    window.setTimeout(run, 900);
  };

  const bindSearchDrawerWarmup = () => {
    document.querySelectorAll(SEARCH_DRAWER_BUTTON_SELECTOR).forEach((button) => {
      ['pointerenter', 'pointerdown', 'touchstart', 'focus'].forEach((eventName) => {
        button.addEventListener(
          eventName,
          () => {
            const sidebar = getSearchSidebar();
            if (sidebar?.dataset.mysticSearchResourceType === 'article') return;

            revealRecentlyViewedSkeleton();
            prewarmRecentlyViewed();
          },
          { passive: true, once: true }
        );
      });
    });
  };

  const watchSearchDrawerSkeleton = () => {
    const sidebar = getSearchSidebar();
    if (!sidebar) return;

    revealRecentlyViewedSkeleton();

    const observer = new MutationObserver(revealRecentlyViewedSkeleton);
    observer.observe(sidebar, { childList: true, subtree: true });
  };

  const init = () => {
    patchSearchSidebarPredictiveFetch();
    watchArticleSearchScrollLock();
    bindArticleFilterButtons();
    patchNativeRecentlyViewedFetch();
    bindSearchDrawerWarmup();
    watchSearchDrawerSkeleton();
    scheduleIdlePrewarm();
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init, { once: true });
    return;
  }

  init();
})();
